import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, CheckCircle2, Archive, Trash2, ChevronDown, ChevronRight, Library } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  active: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-amber-100 text-amber-700',
  retired: 'bg-gray-200 text-gray-500',
};
const SOURCE_LABEL = {
  external_reference: 'External Reference',
  pfap_development_target: 'PFAP Development Target',
  pfap_cohort_reference: 'PFAP Cohort Reference',
  provider_reference: 'Provider Reference',
};

const emptyDraft = () => ({ name: '', domain: 'gps', source_type: 'pfap_development_target', source_name: '' });

const BenchmarkLibrary = () => {
  const { user, hasModuleAccess } = useAuth();
  const canEdit = user?.role === 'Admin' || hasModuleAccess(PM.BENCHMARKS, 'edit');
  const canDelete = user?.role === 'Admin' || hasModuleAccess(PM.BENCHMARKS, 'delete');
  const canApprove = user?.role === 'Admin'; // benchmarks.approve is Admin-managed

  const [sets, setSets] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/benchmarks/sets`);
      setSets(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load benchmark library');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    setExpanded((s) => ({ ...s, [id]: !s[id] }));
    if (!entries[id]) {
      try {
        const r = await axios.get(`${API}/benchmarks/sets/${id}`);
        setEntries((e) => ({ ...e, [id]: r.data.entries || [] }));
      } catch { /* ignore */ }
    }
  };

  const createDraft = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/benchmarks/sets`, draft);
      toast.success('Draft benchmark set created');
      setShowDraft(false); setDraft(emptyDraft()); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create draft'); }
  };

  const act = async (id, action, label) => {
    try {
      if (action === 'delete') await axios.delete(`${API}/benchmarks/sets/${id}`);
      else await axios.post(`${API}/benchmarks/sets/${id}/${action}`);
      toast.success(`Benchmark set ${label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${label}`); }
  };

  const fmtRange = (e) => {
    if (e.representation_type === 'range') return `${e.lower_bound} – ${e.upper_bound}`;
    if (e.representation_type === 'minimum') return `≥ ${e.lower_bound}`;
    if (e.representation_type === 'maximum') return `≤ ${e.upper_bound}`;
    if (e.representation_type === 'target') return `${e.target_value}`;
    if (e.representation_type === 'categorical') return `${e.categorical_value}`;
    return '—';
  };
  const ctxBits = (e) => [
    e.age_min != null || e.age_max != null ? `age ${e.age_min ?? ''}–${e.age_max ?? ''}` : null,
    e.sex, e.position_family, e.protocol_key, e.activity_context,
  ].filter(Boolean).join(' · ') || 'all';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 space-y-5" data-testid="benchmark-library-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Library className="w-6 h-6 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold">Benchmark Library</h1>
              <p className="text-sm text-gray-500">Versioned development targets. Only active sets are used for comparison.</p>
            </div>
          </div>
          {canEdit && (
            <Button onClick={() => setShowDraft(true)} data-testid="benchmark-new-draft-btn">
              <Plus className="w-4 h-4 mr-1" /> New Draft Set
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : sets.length === 0 ? (
          <p className="text-gray-400 text-sm" data-testid="benchmark-empty">No benchmark sets yet.</p>
        ) : (
          <div className="space-y-3">
            {sets.map((s) => (
              <Card key={s.id} data-testid={`benchmark-set-${s.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <button className="flex items-center gap-2 text-left" onClick={() => toggle(s.id)} data-testid={`benchmark-toggle-${s.id}`}>
                      {expanded[s.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <CardTitle className="text-base">{s.name}</CardTitle>
                    </button>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600 uppercase text-[10px]">{s.domain}</Badge>
                      <Badge className={`${STATUS_TONE[s.status]} text-[10px] uppercase`} data-testid={`benchmark-status-${s.id}`}>{s.status}</Badge>
                      <span className="text-xs text-gray-400">v{s.version} · {s.entry_count} entries</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 pl-6">
                    {SOURCE_LABEL[s.source_type] || s.source_type}{s.source_name ? ` — ${s.source_name}` : ''}
                    {s.approved_by_name ? ` · approved by ${s.approved_by_name}` : ''}
                  </p>
                </CardHeader>
                {expanded[s.id] && (
                  <CardContent className="space-y-3">
                    {s.methodology_notes && <p className="text-xs text-gray-500 italic">{s.methodology_notes}</p>}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-gray-400 border-b">
                          <th className="py-1 pr-3">Metric</th><th className="pr-3">Value</th>
                          <th className="pr-3">Unit</th><th className="pr-3">Direction</th><th>Context</th>
                        </tr></thead>
                        <tbody>
                          {(entries[s.id] || []).map((e) => (
                            <tr key={e.id} className="border-b last:border-0" data-testid={`benchmark-entry-${e.id}`}>
                              <td className="py-1 pr-3 font-medium">{e.metric_key}</td>
                              <td className="pr-3">{fmtRange(e)}</td>
                              <td className="pr-3 text-gray-500">{e.unit || '—'}</td>
                              <td className="pr-3 text-gray-500">{e.directionality}</td>
                              <td className="text-gray-400 text-xs">{ctxBits(e)}</td>
                            </tr>
                          ))}
                          {(entries[s.id] || []).length === 0 && (
                            <tr><td colSpan={5} className="py-2 text-gray-400 text-xs">No entries.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {s.status === 'draft' && canApprove && (
                        <Button size="sm" variant="outline" onClick={() => act(s.id, 'approve', 'activated')} data-testid={`benchmark-approve-${s.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve & Activate
                        </Button>
                      )}
                      {s.status === 'active' && canApprove && (
                        <Button size="sm" variant="outline" onClick={() => act(s.id, 'retire', 'retired')} data-testid={`benchmark-retire-${s.id}`}>
                          <Archive className="w-3.5 h-3.5 mr-1" /> Retire
                        </Button>
                      )}
                      {s.status === 'draft' && canDelete && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => act(s.id, 'delete', 'deleted')} data-testid={`benchmark-delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Draft
                        </Button>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showDraft} onOpenChange={setShowDraft}>
          <DialogContent data-testid="benchmark-draft-dialog">
            <DialogHeader><DialogTitle>New Draft Benchmark Set</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="benchmark-draft-name" /></div>
              <div><Label className="text-xs">Domain</Label>
                <Select value={draft.domain} onValueChange={(v) => setDraft({ ...draft, domain: v })}>
                  <SelectTrigger data-testid="benchmark-draft-domain"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gps">GPS</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="biometrics">Biometrics</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Source Type</Label>
                <Select value={draft.source_type} onValueChange={(v) => setDraft({ ...draft, source_type: v })}>
                  <SelectTrigger data-testid="benchmark-draft-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pfap_development_target">PFAP Development Target</SelectItem>
                    <SelectItem value="external_reference">External Reference</SelectItem>
                    <SelectItem value="pfap_cohort_reference">PFAP Cohort Reference</SelectItem>
                    <SelectItem value="provider_reference">Provider Reference</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Source Name (optional)</Label>
                <Input value={draft.source_name} onChange={(e) => setDraft({ ...draft, source_name: e.target.value })} data-testid="benchmark-draft-sourcename" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDraft(false)}>Cancel</Button>
              <Button onClick={createDraft} data-testid="benchmark-draft-save">Create Draft</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BenchmarkLibrary;
